import { BOARDS_ID, FILTER_RULE_ENUMS } from '@constants';

import {
  computeModelData,
  formatDate,
  generateShortId,
  getFullName,
  getShortName,
  sortArray,
} from '@utils';
import { ARNM_ACCIDENTS_TASK_STATUSES_NAMES } from 'components/arnmAccidents/constants';
import { ARNM_MAINTENANCE_TASK_STATUSES_NAMES } from 'components/arnmMaintenance/constants';
import { ARNM_REPAIRS_TASK_STATUSES_NAMES } from 'components/arnmRepairs/constants';
import theme from 'config/theme';

const statusOptions = [
  {
    text: '-',
    value: '',
  },
  {
    text: 'Pending',
    value: 'PENDING',
  },
  {
    text: 'In Progress',
    value: 'IN_PROGRESS',
  },
  {
    text: 'Done',
    value: 'FULFILLED',
  },
];

export const handleServiceVehiclesRequestPayload = (router) => {
  let payload = {};
  const filterRules = [];

  if (router.query?.brand) {
    filterRules.push({
      field: 'vehicleName',
      operator: 9,
      type: 9,
      value: router.query.brand,
    });
  }
  if (router.query?.requestType) {
    filterRules.push({
      field: 'type',
      operator: 0,
      type: 5,
      value: router.query.requestType,
    });
  }
  if (router.query?.status) {
    filterRules.push({
      field: 'status',
      operator: 0,
      type: 5,
      value: router.query.status,
    });
  }
  if ('assignee' in router.query) {
    filterRules.push({
      field: 'assignee',
      operator: 0,
      type: 5,
      value: router.query.assignee === 'none' ? '' : router.query.assignee,
    });
  }

  if (filterRules.length) {
    payload = {
      type: 0,
      field: null,
      operator: null,
      value: filterRules,
    };
  } else {
    payload = {
      type: FILTER_RULE_ENUMS.EmptyRuleType,
    };
  }

  return payload;
};

export const handleServiceVehiclesData = (data) => {
  if (!data || !('serviceVehicles' in data)) {
    return {};
  }

  const array = data?.serviceVehicles?.data || [];
  const types = data?.serviceVehiclesTypes || {};
  const requestTypesOptions = data?.requestTypesOptions || [];
  const assigneesMap = data?.assigneesMap || {};
  const brandsOptions = data?.brandsOptions || [];

  const processedArray = array.map((item) => ({
    status: item.status || '-',
    createdAt: item.createdAt
      ? formatDate(item.createdAt, 'dd/MM/yyyy HH:mm')
      : '-',
    updatedAt: item.updatedAt
      ? formatDate(item.updatedAt, 'dd/MM/yyyy HH:mm')
      : '-',
    startedAt: item.startedAt
      ? formatDate(item.startedAt, 'dd/MM/yyyy HH:mm')
      : '-',
    completedAt: item.completedAt
      ? formatDate(item.completedAt, 'dd/MM/yyyy HH:mm')
      : '-',
    type: types?.[item?.type] || '-',
    subscriber: item?.subscriptionOwnerName || '-',
    vehicleDescription: item?.vehicleName || '-',
    plate: item?.vehiclePlate || '-',
    requestPerson: item?.requestedBy || '-',
    assignee: assigneesMap?.[item?.assignee]?.text || '-',
    title: `${item?.vehicle?.sku?.brand || ''} - ${
      item?.vehicle?.sku?.model || ''
    }`,
    description: `${
      item.startedAt ? formatDate(item.startedAt, 'dd/MM/yyyy HH:mm') : '-'
    } - ${
      item.completedAt ? formatDate(item.completedAt, 'dd/MM/yyyy HH:mm') : '-'
    }`,
    href: `/serviceVehicles/${item?.id}`,
    id: item.id,
  }));

  return {
    services: processedArray,
    columns: [
      {
        id: 0,
        title: 'TODO',
        cardIds: processedArray.map((item) => item?.id),
      },
      {
        id: 1,
        title: 'Doing',
        cardIds: [],
      },
      {
        id: 2,
        title: 'Done',
        cardIds: [],
      },
    ],
    statusOptions,
    brandsOptions,
    requestTypesOptions,
    agentsWithNone: data?.assigneesWithNone || [],
  };
};

export const normalizeVehicleData = (data = {}) => {
  const properties = [];

  if (data.properties?.fuelType) {
    properties.push(data.properties.fuelType);
  }
  if (data.properties?.transmission) {
    properties.push(data.properties.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.year) {
    properties.push(data.year);
  }
  const image = data?.images?.[0]?.url;

  return {
    ...data,
    vehicleId: data.id,
    image,
    title: `${data.manufacturer}, ${data.model}`,
    properties,
    vin: data.vin,
  };
};
export const handleServiceVehicleData = (data, types) => {
  if (!data) {
    return [];
  }
  const fields =
    data.fields?.map((field) => {
      const newField = { ...field };
      if (newField?.type === 'slots') {
        newField.value = newField?.value?.map((value) => {
          return {
            ...value,
            slots: value?.slots?.map((slot) => {
              return { ...slot, vendor: value?.vendor, id: generateShortId() };
            }),
          };
        });
      }
      return newField;
    }) || [];
  const actions = data?.actions?.map((action) => {
    return {
      ...action,
      createdAt: action.createdAt
        ? formatDate(action.createdAt, 'dd/MM/yyyy HH:mm')
        : '-',
    };
  });

  const slots = data?.fields?.filter((field) => {
    return field?.type === 'slots';
  });
  let slot;
  if (slots?.length) {
    slot = slots[0]?.value
      ?.filter((sl) => {
        return sl?.isSelected;
      })
      ?.pop();
  }

  if (data?.images?.length) {
    fields?.push({
      key: 'accidentPhotos',
      type: 'files',
      value: data?.images,
    });
  }

  const serviceVehicle = {
    ...data,
    status: data.status || '-',
    subscriptionOwnerName: data?.subscriptionOwnerName || '-',
    subscriptionOwnerId: data?.user?.id || '-',
    subscriptionDriver: data?.requestedBy || '-',
    requestType: types?.[data?.type] || '-',
    servicePoint: slot ? slot?.vendor : '',
    odometer: data?.vehicle?.mileageFromOdometer
      ? `${data?.vehicle?.mileageFromOdometer?.value} ${data?.vehicle?.mileageFromOdometer?.unitCode}`
      : '-',
    createdAt: data.createdAt
      ? formatDate(data.createdAt, 'dd/MM/yyyy HH:mm')
      : '-',
    updatedAt: data.updatedAt
      ? formatDate(data.updatedAt, 'dd/MM/yyyy HH:mm')
      : '-',
    startedAt: data.startedAt
      ? formatDate(data.startedAt, 'dd/MM/yyyy HH:mm')
      : '-',
    completedAt: data.completedAt
      ? formatDate(data.completedAt, 'dd/MM/yyyy HH:mm')
      : '-',
    type: types?.[data?.type] || '-',
    assignee: data?.assignee,
    vehicleName: data?.vehicleName || '-',
    vehicleId: data?.vehicle?.id || '-',
    internalId: data?.vehicle?.internalId || '-',
    plate: data?.vehiclePlate || '-',
    fields,
    actions,
    vehicle: normalizeVehicleData(data?.vehicle),
  };
  return serviceVehicle;
};

export const handleAgentsData = (data = []) => {
  if (!data) {
    return [];
  }

  return data?.map((item) => ({
    status: item.status || '-',
    text: getFullName(item),
    display: getFullName(item),
    value: item?.email || '-',
    shortName: getShortName(item),
    createdAt: item.createdAt ? formatDate(item.createdAt, 'dd/MM/yyyy') : '-',
    updatedAt: item.updatedAt ? formatDate(item.updatedAt, 'dd/MM/yyyy') : '-',
    email: item?.email || '-',
    firstName: item?.firstName || '-',
    lastName: item?.lastName || '-',
    fullName: getFullName(item),
    role: item?.role || '-',
    id: item.id,
    userName: item.username || '-',
    isActive: item?.isActive,
    statusIsActive: item?.isActive ? 'Active' : 'Inactive',
  }));
};

export const handleComments = (data = [], assignees = []) => {
  const userMap = new Map(
    assignees.map((assignee) => [assignee.email, assignee])
  );
  return sortArray(data, 'createdAt', 'desc').map((item) => {
    const date = item.editedAt || item.createdAt;

    return {
      id: item.id,
      label: 'comment',
      author: item.author,
      shortName: item.author?.slice(0, 2)?.toUpperCase(),
      date: date ? formatDate(date, 'dd MMMM yyyy') : '-',
      time: date ? formatDate(date, 'HH:mm') : '-',
      name: item.isAuthorACustomer
        ? `${item.author} (Customer)`
        : userMap.get(item.author)?.text || item.author,
      assignee: userMap.get(item.assignee)?.text || '-',
      colorName: theme.palette.blues.primaryBlue600,
      comment: item.comment,
      edit: item.editedAt
        ? formatDate(item.editedAt, 'dd MMMM yyyy HH:mm')
        : '',
    };
  });
};

export const normalizeVehicleHistory = (history = []) => {
  const mapTypes = {
    [BOARDS_ID.ARM_ACCIDENTS]: { title: 'New Accident', key: 'arnmAccidents' },
    [BOARDS_ID.ARM_REPAIRS]: { title: 'New Repair', key: 'arnmRepairs' },
    [BOARDS_ID.ARM_MAINTENANCE]: {
      title: 'New Maintenance',
      key: 'arnmMaintenance',
    },
  };

  if (!history.length) {
    return [];
  }

  const status = (item) => {
    if (item.type === BOARDS_ID.ARM_ACCIDENTS) {
      return ARNM_ACCIDENTS_TASK_STATUSES_NAMES[item.status]?.label || '';
    }
    if (item.type === BOARDS_ID.ARM_REPAIRS) {
      return ARNM_REPAIRS_TASK_STATUSES_NAMES[item.status]?.label || '';
    }
    if (item.type === BOARDS_ID.ARM_MAINTENANCE) {
      return ARNM_MAINTENANCE_TASK_STATUSES_NAMES[item.status]?.label || '';
    }
    return '';
  };

  return history.map((item) => ({
    ...item,
    field: [mapTypes[item.type]?.title],
    task: {
      id: item.id,
      link: `/${mapTypes[item.type]?.key}/${item.id}`,
      status: status(item),
    },
  }));
};

export const handleBrandsOptions = (models = []) => {
  if (!models) {
    return [];
  }

  const modelsObjects = computeModelData(models);
  const brandsOptions = [];

  modelsObjects?.manufacturers?.forEach((item) => {
    brandsOptions?.push({
      name: item?.value,
      label: item?.text,
    });
  });
  const dropdownBrandOptions = brandsOptions?.map((option) => {
    return {
      text: option?.label,
      value: option?.name,
    };
  });
  dropdownBrandOptions.unshift({
    text: '-',
    value: '',
  });

  return dropdownBrandOptions;
};

export const handleRequestTypesOptions = (requestTypes) => {
  if (!requestTypes) {
    return [];
  }

  const requestTypesOptions = Object?.keys(requestTypes)?.map((key) => {
    return {
      text: requestTypes[key],
      value: key,
    };
  });
  requestTypesOptions.unshift({
    text: '-',
    value: '',
  });

  return requestTypesOptions;
};
